import React, { ReactElement, useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { AiFillCheckCircle } from "react-icons/ai";
import { FaRocketchat, FaTelegramPlane } from "react-icons/fa";
import { GiGears } from "react-icons/gi";
import { GrAction } from "react-icons/gr";
import { IconType } from "react-icons/lib";
import {
    SiAtlassian, SiGithub, SiHubspot, SiIntercom, SiMicrosoftteams, SiOkta,
    SiSalesforce, SiSlack, SiZapier, SiZendesk,
} from "react-icons/si";
import {
    ActionExecuteStartCondition,
    Chat, DisplayFormSource,
    FieldTypedValue,
    FieldTypedValueBase,
    FieldTypedValueInput,
    FormField, FormFieldType, IntegrationType, IntegrationView,
    TicketInput,
    useGetIntegrationsQuery, useRunIntegrationMutation,
    useSubmitIntegrationMutation,
} from "../../../__generated__/api";
import useKeyboardShortcuts from "../../../contexts/KeyboardShortcutContext";
import { NoDataOrError } from "../../../pages/error/Error";
import ActionExecute from "../../action-execute/ActionExecute";
import { Ticket } from "../../chat/Chat";
import Confirm from "../../confirm/Confirm";
import FieldTypeValueDisplay from "../../form-field/FieldTypeValueDisplay";
import Loading from "../../loading/Loading";
import Styles from "./integrations.module.scss";

interface IntegrationsProps {
    chatId?: string;
    ticket?: Ticket;
    hasStarted: boolean;
    shouldIncludeServices?: boolean;
    onExecuted?: (ticket: TicketInput) => void;
    text?: string;
    className?: string;
}

const ICONS: Record<IntegrationType, IconType> = {
    [IntegrationType.Okta]: SiOkta,
    [IntegrationType.Zendesk]: SiZendesk,
    [IntegrationType.Salesforce]: SiSalesforce,
    [IntegrationType.Github]: SiGithub,
    [IntegrationType.GithubApp]: SiGithub,
    [IntegrationType.Hubspot]: SiHubspot,
    [IntegrationType.Zapier]: SiZapier,
    [IntegrationType.Intercom]: SiIntercom,
    [IntegrationType.Slack]: SiSlack,
    [IntegrationType.SlackAgent]: SiSlack,
    [IntegrationType.SlackBot]: SiSlack,
    [IntegrationType.MicrosoftTeams]: SiMicrosoftteams,
    [IntegrationType.RocketChat]: FaRocketchat,

    [IntegrationType.Integromat]: GiGears,
    [IntegrationType.Atlassian]: SiAtlassian,
    [IntegrationType.Freshdesk]: GiGears,
    [IntegrationType.OneLogin]: GiGears,
    [IntegrationType.ServiceNow]: GiGears,
    [IntegrationType.Workato]: GiGears,

    [IntegrationType.Service]: GiGears,
    [IntegrationType.Approvals]: AiFillCheckCircle,

};

export default function Integrations(
    {
        chatId, ticket, hasStarted, shouldIncludeServices, onExecuted, text, className,
    }: IntegrationsProps,
): ReactElement | null {
    const { data, loading, error } = useGetIntegrationsQuery({
        variables: {
            chatId,
            shouldIncludeServices: shouldIncludeServices ?? true,
        },
    });

    const [runIntegration] = useRunIntegrationMutation();
    const [submit] = useSubmitIntegrationMutation();

    const [shouldShow, setShouldShow] = useState<boolean>(false);
    const [showForm, setShowForm] = useState<boolean>(false);
    const [integrationId, setIntegrationId] = useState<string>();

    const [formId, setFormId] = useState<string>();

    const [values, setValues] = useState<FieldTypedValueInput[]>([]);

    const [form, setForm] = useState<IntegrationView>();

    const [actionId, setActionId] = useState<string>();

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "k+i",
            context: "chat",
            description: "Calls an integration",
            group: "Conversations",
            handler: () => {
                setShouldShow(true);
            },
        });
    });

    useEffect(() => {
        setValues([]);
        setForm(undefined);
    }, [showForm]);

    if (loading) {
        return null;
    }
    if (!data || error) {
        return <NoDataOrError error={error} />;
    }

    let { forms, integrations } = data;
    if (!hasStarted) {
        forms = forms.filter(row => row.actionSettings?.startCondition === ActionExecuteStartCondition.BeforeOrAfterStarted);
        integrations = [];
    }

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const startInteration = async (integrationId: string, formId?: string, search?: string) => {
        setShowForm(true);
        setFormId(formId);
        setIntegrationId(integrationId);
        setShouldShow(false);
        const integration = await runIntegration({
            variables: {
                chatId,
                integrationId,
                formId,
                search,
                source: DisplayFormSource.Portal,
                currentValues: values,
            },
        });
        setForm(integration.data?.runIntegration as IntegrationView | undefined);

        if (!formId) {
            const asyncField = integration.data?.runIntegration?.fields
                .find(formField => formField.type === FormFieldType.AsyncDropdown);
            const selectedFormId = asyncField?.defaultValue?.string;
            if (asyncField && selectedFormId) {
                setFormId(selectedFormId);

                setValues(current => {
                    const copy = [...current];
                    const index = copy.findIndex(fieldValue => fieldValue.fieldId === selectedFormId);
                    const value = {
                        string: selectedFormId,
                        fieldId: asyncField.id,
                        type: asyncField.type,
                    } as FieldTypedValueInput;
                    if (index >= 0) {
                        copy[index] = value;
                    } else {
                        copy.push(value);
                    }
                    return copy;
                });
            }
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const startAction = async (formId?: string) => {
        setActionId(formId);
        setShouldShow(false);
    };

    const onUpdate = (updatedFormId?: string, search?: string) => {
        if (!integrationId) {
            return;
        }
        startInteration(integrationId, updatedFormId ?? formId, search);
    };

    const handleSetValue = (value: FieldTypedValue, field: FormField) => {
        setValues(current => {
            const copy = [...current];
            const index = copy.findIndex(fieldValue => fieldValue.fieldId === value.fieldId);
            if (index >= 0) {
                // eslint-disable-next-line no-param-reassign
                copy[index] = value;
            } else {
                copy.push(value);
            }
            return copy;
        });
        if (value.type === FormFieldType.AsyncDropdown) {
            onUpdate(value.string as string);
        } else if (value.type === FormFieldType.SearchText) {
            onUpdate(undefined, value.string as string);
        } else if (field.optionsSettings?.dynamicChoiceContext || (
            value.type === FormFieldType.Dropdown
            && form?.fields.some(formField => formField.optionsSettings?.dynamicChoiceContext)
        )) {
            const selectedFormId = formId
                ?? form?.fields.find(formField => formField.type === FormFieldType.AsyncDropdown)?.defaultValue?.string;
            onUpdate(selectedFormId as string | undefined);
        }
    };

    const onCreate = () => {
        (async () => {
            await submit({
                variables: {
                    chatId,
                    integrationId,
                    form: {
                        id: formId,
                        values,
                    },
                },
            });
        })();
        setShowForm(false);
    };

    return (
        <>
            {actionId && (
                <ActionExecute
                    actionId={actionId}
                    onHide={() => setActionId(undefined)}
                    chat={ticket as Chat}
                    chatId={chatId}
                    onExecuted={onExecuted}
                />
            )}
            {showForm && (
                <Confirm
                    shouldShow={showForm}
                    onHide={() => setShowForm(false)}
                    confirmText={form?.submit}
                    className={`${Styles["container-dialog"]}`}
                    onConfirm={onCreate}
                >
                    {!form ? (
                        <Loading />
                    ) : (
                        <div className="d-flex flex-column">
                            <h5 className="mb-4">
                                {form.title}
                            </h5>
                            <div className={`overflow-auto flex-grow-1 flex-shrink-1 ${Styles.container}`}>

                                {form.fields.map((field, index) => (

                                    <div className="mb-3" key={field.id}>
                                        <small className="text-gray-500" title={field.description as string}>
                                            {field.name}
                                        </small>
                                        <FieldTypeValueDisplay
                                            field={field}
                                            defaultValue={field.defaultValue ? {
                                                value: field.defaultValue as FieldTypedValueBase,
                                            } : undefined}
                                            fieldNameClassName="text-gray-500"
                                            onChange={value => handleSetValue(value, field)}
                                            shouldShowFieldName={false}
                                            getCurrentForm={() => values}
                                            integrationId={integrationId}
                                            shouldFocus={index === 0}
                                            className="mb-3"
                                        />

                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                </Confirm>
            )}
            {(integrations?.isNotEmpty || forms.isNotEmpty) && (
                <Dropdown show={shouldShow} drop="up">
                    <Dropdown.Toggle
                        variant=""
                        className={`menu-small btn-none p-1 ${className}`}
                        onClick={() => setShouldShow(value => !value)}
                    >
                        <GrAction />
                        {" "}
                        {text}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className={Styles.dropdown}>
                        {forms.isNotEmpty && (
                            <>

                                <Dropdown.Header>Actions</Dropdown.Header>
                                {forms.sortBy("name").map(action => (
                                    <Dropdown.Item
                                        key={action.id}
                                        eventKey={action.id}
                                        onClick={() => startAction(action.id)}
                                    >
                                        <FaTelegramPlane />
                                        {" "}
                                        {action.name}
                                    </Dropdown.Item>
                                ))}
                                {integrations?.isNotEmpty && (
                                    <>
                                        <Dropdown.Divider />
                                        <Dropdown.Header>Services</Dropdown.Header>
                                    </>
                                )}

                            </>
                        )}
                        {integrations?.sortBy("label").map(integration => {
                            const Icon = ICONS[integration.type as IntegrationType] ?? GiGears;
                            return (
                                <Dropdown.Item
                                    key={integration.id}
                                    eventKey={integration.id}
                                    onClick={() => startInteration(integration.id)}
                                >
                                    <Icon />
                                    {" "}
                                    {integration.label}
                                </Dropdown.Item>
                            );
                        })}

                    </Dropdown.Menu>
                </Dropdown>
            )}

        </>

    );
}

Integrations.defaultProps = {
    ticket: undefined,
    shouldIncludeServices: true,
    chatId: undefined,
    onExecuted: undefined,
    text: "",
    className: "",
};
